import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { Button } from '../../../components/ui/button'
import { Badge } from '../../../components/ui/badge'
import { personnelService, type EmployeeFile } from '../../../services'
import { useLocale } from '../../../hooks/use-locale'
import { Upload, Download, Trash2, FileText, AlertCircle } from 'lucide-react'

export default function EmployeeDocumentsSection() {
  const { t } = useTranslation()
  const { formatDateTime } = useLocale()
  const { id } = useParams()
  const queryClient = useQueryClient()

  const { data: docs, isLoading } = useQuery({
    queryKey: ['employee-files', id],
    queryFn: () => personnelService.listEmployeeFiles(id!),
    enabled: !!id,
  })

  const [category, setCategory] = useState<string>('CV')
  const [error, setError] = useState<string | null>(null)
  const [isDownloadingId, setIsDownloadingId] = useState<string | null>(null)

  const documentCategoryOptions = useMemo(
    () => [
      { value: 'CV', label: t('employee.documentCategories.cv') },
      { value: 'CONTRACT', label: t('employee.documentCategories.contract') },
      { value: 'OTHER', label: t('employee.documentCategories.other') },
    ],
    [t]
  )

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) =>
      personnelService.uploadEmployeeFiles({ userId: id!, files, category: category || undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-files', id] })
    },
    onError: (e: any) => {
      setError(e?.userMessage || e?.message || t('common.uploadFailed'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (fileId: string) => personnelService.deleteEmployeeFile(id!, fileId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-files', id] })
    },
    onError: (e: any) => {
      setError(e?.userMessage || e?.message || t('common.deleteFailed'))
    },
  })

  const onUpload = async (files: FileList | null) => {
    setError(null)
    if (!id || !files || files.length === 0) return
    uploadMutation.mutate(Array.from(files))
  }

  const onDownload = async (doc: EmployeeFile) => {
    if (!id) return
    setError(null)
    setIsDownloadingId(doc.id)
    try {
      const blob = await personnelService.downloadEmployeeFile({ userId: id, fileId: doc.id })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = doc.originalName
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (e: any) {
      setError(e?.userMessage || e?.message || t('common.downloadFailed'))
    } finally {
      setIsDownloadingId(null)
    }
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader>
          <CardTitle>{t('employee.uploadDocumentsTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          {error && (
            <div className='flex items-start gap-2 p-3 bg-error-50 border border-error-100 rounded-lg text-error-700 text-sm mb-4'>
              <AlertCircle className='w-4 h-4 mt-0.5 flex-shrink-0' />
              {error}
            </div>
          )}

          <div className='flex flex-col sm:flex-row gap-3 sm:items-end'>
            <div className='flex-1'>
              <label className='block text-sm font-medium text-neutral-700 mb-1.5'>
                {t('common.documentCategory')}
              </label>
              <select
                value={category}
                onChange={e => setCategory(e.target.value)}
                className='w-full px-3 py-2 text-sm border border-neutral-200 bg-white text-neutral-900 rounded-lg focus:ring-2 focus:ring-primary-500/15 focus:border-primary-500 transition-colors duration-150 hover:border-neutral-300'
              >
                {documentCategoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className='inline-flex items-center gap-2 cursor-pointer'>
                <span className='inline-flex items-center justify-center h-9 px-4 rounded-lg text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors'>
                  <Upload className='w-4 h-4 mr-1.5' />
                  {uploadMutation.isPending ? t('employee.uploadingFile') : t('employee.uploadFile')}
                </span>
                <input
                  type='file'
                  multiple
                  className='hidden'
                  onChange={e => onUpload(e.target.files)}
                  disabled={uploadMutation.isPending}
                />
              </label>
              <p className='text-xs text-neutral-400 mt-1'>{t('employee.maxFileSize')}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('employee.savedFiles')}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <div className='w-6 h-6 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin' />
            </div>
          ) : (docs || []).length === 0 ? (
            <div className='flex flex-col items-center justify-center py-12 text-neutral-400'>
              <FileText className='w-10 h-10 mb-3 text-neutral-300' />
              <p className='text-sm'>{t('employee.noDocumentsYet')}</p>
            </div>
          ) : (
            <div className='divide-y divide-neutral-100'>
              {(docs || []).map(doc => (
                <div key={doc.id} className='flex items-center justify-between gap-4 py-3'>
                  <div className='flex items-center gap-3 min-w-0'>
                    <div className='w-8 h-8 rounded-lg bg-neutral-100 flex items-center justify-center flex-shrink-0'>
                      <FileText className='w-4 h-4 text-neutral-500' />
                    </div>
                    <div className='min-w-0'>
                      <div className='text-sm font-medium text-neutral-900 truncate'>
                        {doc.originalName}
                      </div>
                      <div className='text-xs text-neutral-400'>
                        {formatDateTime(doc.createdAt)}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2 flex-shrink-0'>
                    <Badge title={doc.category || t('common.dash')} variant='primary' />
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => onDownload(doc)}
                      disabled={isDownloadingId === doc.id}
                    >
                      <Download className='w-4 h-4' />
                    </Button>
                    <Button
                      variant='ghost'
                      size='sm'
                      onClick={() => deleteMutation.mutate(doc.id)}
                      disabled={deleteMutation.isPending}
                      className='text-error-500 hover:text-error-700 hover:bg-error-50'
                    >
                      <Trash2 className='w-4 h-4' />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
